import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WBAlert,
  WBBox,
  WBButton,
  WBIconButton,
  WBTextField,
} from '@admiin-com/ds-web';
import { updatePassword } from 'aws-amplify/auth';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface ChangeFormData {
  oldPassword: string;
  password: string;
  confirmPassword: string;
}

const resolver =
  <T extends z.ZodTypeAny>(schema: T) =>
  async (values: unknown) => {
    const result = schema.safeParse(values);
    if (result.success) {
      return { values: result.data, errors: {} };
    }
    const formErrors: Record<string, { type: string; message: string }> = {};
    const fieldErrors = result.error.flatten().fieldErrors;
    Object.keys(fieldErrors).forEach((key) => {
      const message = fieldErrors[key]?.[0];
      if (message) {
        formErrors[key] = { type: 'validation', message };
      }
    });
    return { values: {}, errors: formErrors };
  };

const ChangePassword = () => {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isOldPasswordVisible, setIsOldPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  const schema = z
    .object({
      oldPassword: z.string().min(1, t('oldPasswordRequired') as string),
      password: z.string().min(1, t('newPasswordRequired') as string),
      confirmPassword: z
        .string()
        .min(1, t('confirmPasswordRequired') as string),
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: t('confirmNotMatch') as string,
      path: ['confirmPassword'],
    });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangeFormData>({ resolver: resolver(schema) });

  const onSubmit = async ({ oldPassword, password }: ChangeFormData) => {
    setError('');
    setSuccess(false);
    try {
      await updatePassword({ oldPassword, newPassword: password });
      setSuccess(true);
      reset();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    }
  };

  const renderPasswordToggle = (
    isPasswordVisible: boolean,
    onToggleVisibility: (value: boolean) => void
  ) => (
    <WBIconButton
      size="small"
      icon={isPasswordVisible ? 'EyeOff' : 'Eye'}
      aria-label={
        isPasswordVisible ? t('hidePasswordAria') : t('showPasswordAria')
      }
      onClick={(event) => {
        event.preventDefault();
        onToggleVisibility(!isPasswordVisible);
      }}
    />
  );

  return (
    <WBBox
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      maxWidth={400}
      mx="auto"
      mt={8}
    >
      {error && (
        <WBAlert severity="error" sx={{ mb: 2 }}>
          {error}
        </WBAlert>
      )}
      {success && (
        <WBAlert severity="success" sx={{ mb: 2 }}>
          {t('changePasswordTitle')} {t('success')}
        </WBAlert>
      )}
      <WBTextField
        fullWidth
        type={isOldPasswordVisible ? 'text' : 'password'}
        label={t('oldPasswordTitle')}
        margin="normal"
        {...register('oldPassword')}
        error={!!errors.oldPassword}
        helperText={errors.oldPassword?.message}
        rightIcon={renderPasswordToggle(
          isOldPasswordVisible,
          setIsOldPasswordVisible
        )}
      />
      <WBTextField
        fullWidth
        type={isNewPasswordVisible ? 'text' : 'password'}
        label={t('newPasswordTitle')}
        margin="normal"
        {...register('password')}
        error={!!errors.password}
        helperText={errors.password?.message}
        rightIcon={renderPasswordToggle(
          isNewPasswordVisible,
          setIsNewPasswordVisible
        )}
      />
      <WBTextField
        fullWidth
        type={isConfirmPasswordVisible ? 'text' : 'password'}
        label={t('confirmPasswordTitle')}
        margin="normal"
        {...register('confirmPassword')}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword?.message}
        rightIcon={renderPasswordToggle(
          isConfirmPasswordVisible,
          setIsConfirmPasswordVisible
        )}
      />
      <WBButton type="submit" fullWidth sx={{ mt: 2 }}>
        {t('changePasswordTitle')}
      </WBButton>
    </WBBox>
  );
};

export default ChangePassword;
